import { useEffect, useState } from "react";
import * as XLSX from "xlsx-js-style";
import { settings } from "../settings";

const STATUS_CONFIG = {
  Success: { cls: "status-success", icon: "/images/icon-success.png" },
  Failed: { cls: "status-failed", icon: "/images/icon-failed.png" },
  // Processing: { cls: "status-processing", icon: "/images/icon-processing.png" },
  Retrying: { cls: "status-retrying", icon: "/images/icon-processing.png" },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { cls: "" };
  return <span className={`status-badge ${cfg.cls}`}>{status}</span>;
}

function SummaryCards({ logs, getStatus }) {
  const total = logs.length;
  const successful = logs.filter(
    (r) => getStatus(r.RetryCount) === "Success",
  ).length;
  const retrying = logs.filter(
    (r) => getStatus(r.RetryCount) === "Retrying",
  ).length;
  const failed = logs.filter(
    (r) => getStatus(r.RetryCount) === "Failed",
  ).length;
  const successPct = total ? Math.round((successful / total) * 100) : 0;

  const cards = [
    {
      label: "Total",
      value: total,
      pct: null,
      icon: "/images/icon-logs.png",
      cls: "card-total",
    },
    {
      label: "Successful",
      value: successful,
      pct: `${successPct}%`,
      icon: "/images/icon-success.png",
      cls: "card-success",
    },
    {
      label: "Retrying",
      value: retrying,
      pct: null,
      icon: "/images/icon-processing.png",
      cls: "card-processing",
    },
    {
      label: "Failed",
      value: failed,
      pct: null,
      icon: "/images/icon-failed.png",
      cls: "card-failed",
    },
  ];

  return (
    <div className="summary-cards">
      {cards.map((c) => (
        <div key={c.label} className={`summary-card ${c.cls}`}>
          <div className="card-icon">
            <img src={c.icon} alt={c.label} className="card-icon-img" />
          </div>
          <div className="card-body">
            <p className="card-label">{c.label}</p>
            <p className="card-value">{c.pct ?? c.value}</p>
            {c.pct && <p className="card-sub">{c.value} records</p>}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function EmailTriageLogs() {
  const [logs, setLogs] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  useEffect(() => {
    fetch("/api/email-triage-logs")
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((json) => {
        setLogs(json.data || []);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  const VISIBLE_COLUMNS = [
    "SenderEmail",
    "Subject",
    "ExceptionMessage",
    "RetryCount",
    "CreatedDate",
    "TicketNo",
    "ConfidenceScore"
  ];
  const COLUMN_LABELS = {
    SenderEmail: "Sender Email",
    Subject: "Subject",
    ExceptionMessage: "Exception Message",
    RetryCount: "Status",
    CreatedDate: "Sent Date",
    TicketNo: "Ticket No",
    ConfidenceScore: "Confidence Score"
  };

  const columns =
    logs.length > 0
      ? Object.keys(logs[0]).filter((col) => VISIBLE_COLUMNS.includes(col))
      : [];

  const isEmpty = !loading && !error && logs.length === 0;

  const getStatus = (retryCount) => {
    if (retryCount >= 3) return "Failed";
    if (retryCount === 2 || retryCount === 1) return "Retrying";
    return "Success";
  };

  // Global search
  const searched = logs.filter((row) =>
    columns.some((col) => {
      const cellVal =
        col === "RetryCount"
          ? getStatus(row[col])
          : col.toLowerCase().includes("date")
            ? formatDate(row[col])
            : String(row[col] ?? "");
      return cellVal.toLowerCase().includes(search.toLowerCase());
    }),
  );

  // Status dropdown filter
  const filtered =
    statusFilter === "All"
      ? searched
      : searched.filter((row) => getStatus(row.RetryCount) === statusFilter);

  // Date range filter
  const dateFiltered = filtered.filter((row) => {
    if (!startDate && !endDate) return true;
    const created = row.CreatedDate ? new Date(row.CreatedDate) : null;
    if (!created || isNaN(created)) return true;
    const start = startDate ? new Date(startDate) : null;
    const end = endDate
      ? new Date(new Date(endDate).setHours(23, 59, 59, 999))
      : null;
    if (start && created < start) return false;
    if (end && created > end) return false;
    return true;
  });

  // Sorting
  const sorted = [...dateFiltered].sort((a, b) => {
    if (!sortCol) return 0;
    const aVal = a[sortCol] ?? "";
    const bVal = b[sortCol] ?? "";
    const cmp = String(aVal).localeCompare(String(bVal), undefined, {
      numeric: true,
    });
    return sortDir === "asc" ? cmp : -cmp;
  });

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const pageData = sorted.slice((page - 1) * pageSize, page * pageSize);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };
  const handleStatusFilter = (e) => {
    setStatusFilter(e.target.value);
    setPage(1);
  };
  const handleStartDate = (e) => {
    setStartDate(e.target.value);
    setPage(1);
  };
  const handleEndDate = (e) => {
    setEndDate(e.target.value);
    setPage(1);
  };
  const handlePageSize = (e) => {
    setPageSize(Number(e.target.value));
    setPage(1);
  };

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortCol(col);
      setSortDir("asc");
    }
    setPage(1);
  };

  const sortIcon = (col) => {
    if (sortCol !== col) return " ⇅";
    return sortDir === "asc" ? " ↑" : " ↓";
  };

  const renderCell = (col, row) => {
    if (col === "RetryCount") {
      return <StatusBadge status={getStatus(row[col])} />;
    }
    if (col.toLowerCase().includes("date")) {
      return formatDate(row[col]);
    }
    if (col === "TicketNo") {
      return row[col] ? (
        <a
          href={`${settings.ticketBaseUrl}${row[col]}`}
          target="_blank"
          rel="noopener noreferrer"
          className="ticket-link"
        >
          {row[col]}
        </a>
      ) : "-";
    }
    return String(row[col] ?? "");
  };

  // Export only what's currently visible in the grid (sorted + filtered)
  const exportToExcel = () => {
    if (sorted.length === 0) {
      alert("No records to export.");
      return;
    }
    const exportColumns = ["SenderEmail", "Subject", "ExceptionMessage", "RetryCount", "CreatedDate", "TicketNo", "ConfidenceScore", "EmailContent"];
    const exportedAt = new Date().toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
    });

    // â”€â”€ Build filter summary lines 
    const filterLines = [];

    // Line 1: Report title + exported timestamp
    filterLines.push({
      A: "Email Triage Logs Report",
      B: "",
      C: "",
      D: "",
      E: `Exported: ${exportedAt}`,
      F: "",
      G: "",
      H: ""
    });

    // Line 2: Applied filters
  const parts = [];
if (search)   parts.push(`Search: "${search}"`);
parts.push(`Status: ${statusFilter === "All" ? "All Status" : statusFilter}`);
if (startDate) parts.push(`From: ${startDate}`);
if (endDate)   parts.push(`To: ${endDate}`);
const filterText = `Filters Applied - ${parts.join("   |   ")}`;
    // Line 3: blank separator
    filterLines.push({ A: "", B: "", C: "", D: "", E: "", F: "", G: "", H: "" });
    const sheetData = sorted.map((row) =>
      exportColumns.reduce((acc, col) => {
        if (col === "RetryCount") {
          acc["Status"] = getStatus(row[col]);
        }         
        else if (col === "CreatedDate") { acc["Sent Date"] = formatDate(row[col]); }
        else if (col === "SenderEmail") { acc["Sender Email"] = row[col] ?? ""; }
        else if (col === "ExceptionMessage") { acc["Exception Message"] = row[col] ?? ""; }
        else if (col === "TicketNo") { acc["Ticket No"] = row[col] ?? ""; }
        else if (col === "ConfidenceScore")  { acc["Confidence Score"]  = row[col] ?? ""; }
        else if (col === "EmailContent")  { acc["Email Content"]  = row[col] ?? ""; }
        else { acc[col] = row[col] ?? ""; }
        return acc;
      }, {}),
    );
    // â”€â”€ Build worksheet from filter lines + header + data â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    const wsData = [
      // Row 1 â€” Report title
      ["Email Triage Logs Report", "", "", "", `Exported: ${exportedAt}`, ""],
      // Row 2 â€” Filter summary
      [filterText, "", "", "", `Total Records: ${sorted.length}`, ""],
      // Row 3 â€” blank
      ["", "", "", "", "", ""],
      // Row 4 â€” column headers
      ["Sender Email", "Subject", "Exception Message", "Status", "Sent Date", "Ticket No", "Confidence Score", "Email Content"],
      // Rows 5+ â€” data
      ...sheetData.map((r) => [
        r["Sender Email"], r["Subject"], r["Exception Message"],
        r["Status"], r["Sent Date"], r["Ticket No"], r["Confidence Score"], r["Email Content"]
      ]),
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(wsData);

    // â”€â”€ Column widths â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    worksheet["!cols"] = [
      { wch: 35 }, // Sender Email
      { wch: 40 }, // Subject
      { wch: 50 }, // Exception Message
      { wch: 12 }, // Status
      { wch: 22 }, // Sent Date
      { wch: 15 }, // Ticket No
      { wch: 20 }, // ConfidenceScore
      { wch: 50 }, // Email Content
    ];

    // â”€â”€ Merge title cells A1:D1 and filter cells A2:D2 â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    worksheet["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 3 } }, // A1:D1 title
      { s: { r: 1, c: 0 }, e: { r: 1, c: 3 } }, // A2:D2 filter line
    ];

    // â”€â”€ Cell styles (bold title, grey filter row, blue header row) â”€â”€â”€â”€â”€â”€â”€â”€
    // â”€â”€ Shared border definition â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    const border = {
      top: { style: "thin", color: { rgb: "CBD5E1" } },
      bottom: { style: "thin", color: { rgb: "CBD5E1" } },
      left: { style: "thin", color: { rgb: "CBD5E1" } },
      right: { style: "thin", color: { rgb: "CBD5E1" } },
    };
    const headerBorder = {
      top: { style: "medium", color: { rgb: "1E3A8A" } },
      bottom: { style: "medium", color: { rgb: "1E3A8A" } },
      left: { style: "thin", color: { rgb: "3B5FD6" } },
      right: { style: "thin", color: { rgb: "3B5FD6" } },
    };

    // â”€â”€ Cell styles (bold title, grey filter row, blue header row) â”€â”€â”€â”€â”€â”€â”€â”€
    const titleCell = worksheet["A1"];
    if (titleCell) {
      titleCell.s = {
        font: { bold: true, sz: 14, color: { rgb: "1E3A8A" } },
        fill: { fgColor: { rgb: "EFF6FF" } },
        border,
      };
    }
    const filterCell = worksheet["A2"];
    if (filterCell) {
      filterCell.s = {
        font: { italic: true, sz: 10, color: { rgb: "475569" } },
        fill: { fgColor: { rgb: "F8FAFC" } },
        border,
      };
    }

    // Header row (row index 3 = row 4)
    ["A4", "B4", "C4", "D4", "E4", "F4", "G4", "H4"].forEach((addr) => {
      if (worksheet[addr]) {
        worksheet[addr].s = {
          font: { bold: true, color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: "193CB8" } },
          alignment: { horizontal: "center" },
          border: headerBorder,
        };
      }
    });

    // â”€â”€ Status colour coding â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    const statusColors = {
      Success: { bg: "DCFCE7", fg: "15803D" },
      Failed: { bg: "FEE2E2", fg: "B91C1C" },
      Retrying: { bg: "FEF3C7", fg: "B45309" },
    };
    sheetData.forEach((row, i) => {
      const cellAddr = `D${i + 5}`; // data starts at row 5
      const color = statusColors[row["Status"]];
      if (color && worksheet[cellAddr]) {
        worksheet[cellAddr].s = {
          font: { bold: true, color: { rgb: color.fg } },
          fill: { fgColor: { rgb: color.bg } },
          alignment: { horizontal: "center" },
          border,
        };
      }
      // Zebra rows
      ["A", "B", "C", "E", "F", "G", "H"].forEach((col) => {
        const addr = `${col}${i + 5}`;
        if (worksheet[addr]) {
          worksheet[addr].s = {
            fill: { fgColor: { rgb: i % 2 === 0 ? "FFFFFF" : "F8FAFC" } },
            alignment: { wrapText: true, vertical: "top" },
            border,
          };
        }
      });
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Email Triage Logs");
    XLSX.writeFile(workbook, `email-triage-logs-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  if (loading)
    return (
      <div className="text-center">
        <img src="/images/lnw-loader.gif" alt="Loading.." />
        <h2 className="text-center">Loading ..!</h2>
      </div>
    );

  if (error) return <div className="triage-error">Error: {error}</div>;

  return (
    <div className="p-3">
      <h3>Email Triage Logs</h3>

      {/* Summary Cards */}
      <SummaryCards logs={dateFiltered} getStatus={getStatus} />

      {!isEmpty && (
        <div className="triage-filter">
          <input
            className="triage-search"
            type="search"
            placeholder="🔍  Search logs by subject, ticket, or email…"
            value={search}
            onChange={handleSearch}
          />

          {/* Status dropdown */}
          <select
            className="triage-select"
            value={statusFilter}
            onChange={handleStatusFilter}
          >
            <option value="All">All Status</option>
            <option value="Success">Success</option>
            <option value="Retrying">Retrying</option>
            <option value="Failed">Failed</option>
          </select>
          {/* Date range */}
          <div className="triage-date-range">
            <label className="date-label">From</label>
            <input
              className="triage-date"
              type="date"
              value={startDate}
              onChange={handleStartDate}
              max={endDate || undefined}
            />
            <label className="date-label">To</label>
            <input
              className="triage-date"
              type="date"
              value={endDate}
              onChange={handleEndDate}
              min={startDate || undefined}
            />
            {(startDate || endDate) && (
              <button
                className="triage-clear-date"
                onClick={() => {
                  setStartDate("");
                  setEndDate("");
                  setPage(1);
                }}
                title="Clear dates"
              >
                ✕
              </button>
            )}
          </div>

          {/* Export current grid */}
          <button onClick={exportToExcel}>Export</button>
        </div>
      )}

      {isEmpty && (
        <div className="triage-empty-state">
          <svg width="52" height="52" fill="none" viewBox="0 0 24 24">
            <rect
              x="3"
              y="3"
              width="18"
              height="18"
              rx="3"
              stroke="#cbd5e1"
              strokeWidth="1.5"
            />
            <path d="M3 8h18" stroke="#cbd5e1" strokeWidth="1.5" />
            <path
              d="M8 12h8M8 16h5"
              stroke="#cbd5e1"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
          <p className="no-records">No records found</p>
          <p className="no-records-subtitile">
            The Email_Triage table is currently empty.
          </p>
        </div>
      )}

      {/* Table */}
      {!isEmpty && (
        <div className="table-wrapper">
          <table className="triage-table">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col} onClick={() => handleSort(col)}>
                    {COLUMN_LABELS[col] || col}
                    <span className="sort-icon">{sortIcon(col)}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageData.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="text-center">
                    No matching records.
                  </td>
                </tr>
              ) : (
                pageData.map((row, i) => (
                  <tr key={i} className="triage-row">
                    {columns.map((col) => (
                      <td
                        key={col}
                        className="triage-td"
                        title={col !== "RetryCount" ? String(row[col] ?? "") : undefined}
                      >
                        {renderCell(col, row)}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
      <div className="d-flex p-3">
        {!isEmpty && totalPages > 1 && (
          <div className="triage-pagination">
            <button onClick={() => setPage(1)} disabled={page === 1}>
              «
            </button>
            <button onClick={() => setPage((p) => p - 1)} disabled={page === 1}>
              ‹
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(
                (p) => p === 1 || p === totalPages || Math.abs(p - page) <= 2,
              )
              .reduce((acc, p, idx, arr) => {
                if (idx > 0 && p - arr[idx - 1] > 1)
                  acc.push(
                    <span key={`ellipsis-${p}`} className="pg-info">
                      …
                    </span>,
                  );
                acc.push(
                  <button
                    key={p}
                    className={`pagination-btn ${p === page ? "active" : ""}`}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </button>,
                );
                return acc;
              }, [])}
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={page === totalPages}
            >
              ›
            </button>
            <button
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
            >
              »
            </button>
            <span className="pg-info">
              {(page - 1) * pageSize + 1}–
              {Math.min(page * pageSize, sorted.length)} of {sorted.length}
            </span>
          </div>
        )}
        {/* Rows per page */}
        <div className="triage-row-per-page">
          <label className="page-size-label">Rows per page:</label>
          <select
            className="triage-select"
            value={pageSize}
            onChange={handlePageSize}
          >
            {[10, 20, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (isNaN(date)) return value;
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};
